import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import IMG_EMBED_CODE from 'assets/images/online-booking/ncob-embed-code.png';
import IMG_URL_CODE from 'assets/images/online-booking/ncob-url-code.png';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import CONF from 'config/conf.singleton';
import DELAY from 'constants/delay.const';
import SALON_FLAG from 'constants/salonFlag.const';
import S from 'constants/string.const';
import { useHelpLinks } from 'hooks/useHelpLinks.hook';
import { useNcobRedirectUrl } from 'hooks/useNcobRedirectUrl.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId, useSingleSalonFlag } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonGroupUrl, selectCurrentSalonTinyUrl } from 'selectors/currentSalon.selectors';
import { isEffectiveReceptionist } from 'selectors/user';
import cpSet from 'utils/dom/clipboard/cpSet.util';
import delayed from 'utils/functions/delayed.util';

import CN from './BookingCode.module.scss';

const useCopy = () => {
  const { val: copied, yay: startCopy, nay: resetCopy } = useTip(false);

  const handleCopy = () => {
    startCopy();
    delayed(DELAY.attention, resetCopy);
  };

  return { copied, handleCopy };
};

export const BookingCode = ({ salonGroupName, isChecked, onCheckboxClick }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { bookingHelpLink } = useHelpLinks();
  const salonId = useCurrentSalonId();

  const isReceptionist = useSelector(isEffectiveReceptionist);
  const tinyUrl = useSelector(selectCurrentSalonTinyUrl);
  const groupNcobTinyUrl = useSelector(selectCurrentSalonGroupUrl);

  const isNewNcob = useSingleSalonFlag(SALON_FLAG.useNewNcob);
  const effectiveTinyUrl = useNcobRedirectUrl({ ncobTinyUrl: tinyUrl, salonId });
  const embedBookingUrl = isNewNcob ? `${CONF.ncobV2BaseUrl.replace(/\/salon$/, '')}/#/salon/${salonId}/` : `${CONF.bookingUrlRoot}${salonId}/`;

  const { copied: copiedTiny, handleCopy: handleCopyTiny } = useCopy();
  const { copied: copiedGroup, handleCopy: handleCopyGroup } = useCopy();

  const { copied: copiedJs, handleCopy: handleCopyJs } = useCopy();
  const { val: isModalOpen, yay: openModal, nay: closeModal } = useTip(false);

  const embedJsCode = useMemo(
    () => `<script
        src="https://embed.getslick.com/embed-booking.js"
        data-booking-url="${embedBookingUrl}">
      </script>`,
    [embedBookingUrl],
  );

  return (
    <div
      className={cn({
        [CN.component]: true,
        [CN.disabled]: !isChecked,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.header}>
        <div className={CN.headerContent}>
          <div className={CN.pageTitle}>
            {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.title')}
            <div className={CN.gap}>
              <Badge variant="fine" name={t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.badge')} />
            </div>
          </div>

          {isReceptionist ? (
            <Tooltip variant="top" content={<TooltipContentReceptionist />}>
              <Toggler tag="BookingCode-toggle" selected={isChecked} />
            </Tooltip>
          ) : (
            <Toggler tag="BookingCode-toggle" onClick={onCheckboxClick} selected={isChecked} />
          )}
        </div>
      </div>
      <div className={CN.containerBoxes}>
        {/* Booking Link */}
        <div data-bem="BookingCode__booking--link" className={CN.box}>
          <div className={CN.boxTitle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.box1Title')}</div>
          <div className={CN.boxSubTitle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.box1SubTitle')}</div>
          <div className={cn(CN.imageBox, CN.urlCode)}>
            <img data-bem="BookingCode__img" src={IMG_URL_CODE} className={CN.image} alt="" />
          </div>

          <div className={CN.boxContent}>
            <div className={CN.content}>
              <div className={CN.urlTitle}>
                {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.box1Guide1')}
                <span className={CN.url}>{effectiveTinyUrl}</span>
              </div>

              <Button
                tag="BookingCode__button--tiny--code"
                variant="primary"
                noMargin
                disabled={!isChecked}
                onClick={async () => {
                  await cpSet(effectiveTinyUrl);
                  handleCopyTiny();
                }}
              >
                {copiedTiny ? t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copied') : t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copy')}
              </Button>
            </div>

            {salonGroupName && (
              <div className={CN.content}>
                <div className={CN.urlTitle}>
                  {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.box1Guide2')}
                  <span className={CN.url}>{groupNcobTinyUrl}</span>
                </div>

                <Button
                  tag="BookingCode__button--group--code"
                  variant="primary"
                  noMargin
                  disabled={!isChecked}
                  onClick={async () => {
                    await cpSet(groupNcobTinyUrl);
                    handleCopyGroup();
                  }}
                >
                  {copiedGroup ? t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copied') : t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copy')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Embed Code */}
        <div data-bem="BookingCode__embed--code" className={CN.box}>
          <div className={CN.boxTitle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.box2Title')}</div>
          <div className={CN.boxSubTitle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.box2SubTitle')}</div>
          <div className={cn(CN.imageBox, CN.embedCode)}>
            <img data-bem="BookingCode__img" src={IMG_EMBED_CODE} className={CN.image} alt="" />
          </div>
          <div className={CN.embeddedText}>
            {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.box2Guide')}
            <Button variant="secondary" noMargin onClick={openModal}>
              <div className={CN.buttonText}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.viewEmbedCode')}</div>
            </Button>
          </div>
        </div>
      </div>
      <div className={CN.additionalInfo}>
        {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.guide')}
        <div className={CN.articleLink}>
          <ExternalLink link={bookingHelpLink} variant="link">
            <span className={CN.link}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.viewArticles')}</span>
          </ExternalLink>
        </div>
      </div>
      <SalonModal tag="BookingCode__templates" isOpen={isModalOpen} variant="mediumWithHeaderOnly">
        <SalonModal.Header>
          {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.modalTitle')}
          <Button variant="empty" onClick={closeModal}>
            <Icon variant="close" />
          </Button>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.scrollContainer}>
            {/* tiny url */}
            <div className={CN.infoUrl}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.placeUrlMsg')}</div>
            <div className={CN.copyRow}>
              <code className={CN.codeText}>{effectiveTinyUrl}</code>
              <Button
                tag="BookingCode__button--tiny--code"
                variant="primary"
                noMargin
                disabled={!isChecked}
                onClick={async () => {
                  await cpSet(effectiveTinyUrl);
                  handleCopyTiny();
                }}
              >
                {copiedTiny ? t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copied') : t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copyUrl')}
              </Button>
            </div>
            <div className={CN.infoUrl}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.bookingEmbedCode')}</div>
            <div className={CN.copyFilesContainer}>
              {/* js */}
              <div className={CN.copyRow}>
                <code className={CN.codeText}>{embedJsCode}</code>
                <Button
                  tag="BookingCode__js-code"
                  variant="primaryForce"
                  noMargin
                  onClick={async () => {
                    await cpSet(embedJsCode);
                    handleCopyJs();
                  }}
                >
                  {copiedJs ? t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copied') : t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.copyJs')}
                </Button>
              </div>
              <AlertPanel tag="AlertPanel__alert-warning" variant="warning">
                <div className={CN.instructions}>
                  <div className={CN.instructionsTitle}>
                    <div className={CN.textBold}>{t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.modalAlertPart1')}</div>
                    {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.modalAlertPart2')}
                  </div>

                  <div>
                    {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.modalAlertPart3')}
                    <code className={CN.code}>
                      {S.lt}
                      {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.footer')}
                      {S.gt}
                    </code>
                    {t('common.or')}
                    <code className={CN.code}>
                      {S.lt}
                      {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.body')}
                      {S.gt}
                    </code>
                    {t('onlineBooking.generalSettings.socialWebsiteLinks.onlineBooking.modalAlertPart4')}
                  </div>
                </div>
              </AlertPanel>
            </div>
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};
